import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useEnrolledExams from '../hooks/useEnrolledExams';
import { useOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import { bankUploadApi, apiErrorMessage } from '../services/api';
import { Button, Card, PageSkeleton, ErrorState, EmptyState, Spinner } from '../components/ui';
import UploadBankDialog from '../components/dialogs/UploadBankDialog';

const PAGE_SIZE = 10;

export default function QuestionBanksPage() {
  const navigate = useNavigate();
  const org = useOrg();
  const toast = useToast();
  const { exams, loading: examsLoading, error: examsError } = useEnrolledExams();

  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(true);
  const [uploadsError, setUploadsError] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // Filters and search
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | published | draft | needs_review | processing | error
  const [selectedExamId, setSelectedExamId] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // table | grid
  const [page, setPage] = useState(0);

  // Retrying job state
  const [retryingJobId, setRetryingJobId] = useState(null);

  const loadUploads = useCallback(async () => {
    if (!org?.id) return;
    setUploadsLoading(true);
    setUploadsError(null);
    try {
      const res = await bankUploadApi.listBanks(org.id);
      const items = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.jobs)
          ? res.data.jobs
          : Array.isArray(res.data?.items)
            ? res.data.items
            : [];
      setUploads(items);
    } catch (err) {
      setUploadsError(apiErrorMessage(err, 'Failed to load institute question banks'));
    } finally {
      setUploadsLoading(false);
    }
  }, [org?.id]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  // Handle retrying a failed or stuck job
  const handleRetryJob = async (e, jobId) => {
    e.stopPropagation();
    if (!jobId || retryingJobId) return;
    setRetryingJobId(jobId);
    try {
      await bankUploadApi.retryJob(org.id, jobId);
      toast.success('Extraction job restarted! PDF has been re-queued for processing.');
      await loadUploads();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to restart extraction job'));
    } finally {
      setRetryingJobId(null);
    }
  };

  const getStatusChip = (job) => {
    const status = (job.status || 'UPLOADED').toUpperCase();
    const phase = job.phase;

    if (status === 'COMPLETED') {
      return <span className="status">✓ Completed</span>;
    }
    if (status === 'COMPLETED_WITH_ERRORS') {
      return (
        <span className="pending" style={{ background: 'var(--amber-soft)', color: 'var(--amber)' }}>
          Completed with warnings
        </span>
      );
    }
    if (status === 'PROCESSING') {
      return (
        <span className="pending">
          <Spinner /> Processing {phase ? `(${phase})` : ''}
        </span>
      );
    }
    if (status === 'ERROR' || status === 'FAILED' || status === 'UPLOAD_FAILED') {
      return <span className="tag" style={{ background: 'var(--red-soft)', color: 'var(--red)' }}>Failed</span>;
    }
    return <span className="pending">Uploaded</span>;
  };

  const getReviewStatusChip = (job) => {
    const reviewStatus = (job.reviewStatus || 'draft').toLowerCase();
    if (reviewStatus === 'published') {
      return <span className="tag" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}>Published</span>;
    }
    if (reviewStatus === 'archived') {
      return <span className="tag" style={{ background: '#eceff4', color: 'var(--muted)' }}>Archived</span>;
    }
    return <span className="tag" style={{ background: '#eef1f5', color: 'var(--muted)' }}>Draft</span>;
  };

  // High-level statistics
  const stats = useMemo(() => {
    const total = uploads.length;
    const published = uploads.filter((j) => j.reviewStatus === 'published').length;
    const drafts = uploads.filter((j) => j.reviewStatus === 'draft' || !j.reviewStatus).length;
    const needsReview = uploads.filter((j) => (j.totalNeedsReview || 0) > 0).length;
    const processing = uploads.filter((j) => (j.status || '').toUpperCase() === 'PROCESSING' || (j.status || '').toUpperCase() === 'UPLOADED').length;
    const errors = uploads.filter((j) => {
      const s = (j.status || '').toUpperCase();
      return s === 'ERROR' || s === 'FAILED' || s === 'UPLOAD_FAILED';
    }).length;
    const totalQuestions = uploads.reduce((acc, j) => acc + (j.totalQuestionsWritten || j.totalQuestionsExtracted || 0), 0);

    return { total, published, drafts, needsReview, processing, errors, totalQuestions };
  }, [uploads]);

  // Filtered and searched uploads list
  const filteredUploads = useMemo(() => {
    let list = uploads;

    if (statusFilter === 'published') {
      list = list.filter((j) => j.reviewStatus === 'published');
    } else if (statusFilter === 'draft') {
      list = list.filter((j) => j.reviewStatus === 'draft' || !j.reviewStatus);
    } else if (statusFilter === 'needs_review') {
      list = list.filter((j) => (j.totalNeedsReview || 0) > 0);
    } else if (statusFilter === 'processing') {
      list = list.filter((j) => {
        const s = (j.status || '').toUpperCase();
        return s === 'PROCESSING' || s === 'UPLOADED';
      });
    } else if (statusFilter === 'error') {
      list = list.filter((j) => {
        const s = (j.status || '').toUpperCase();
        return s === 'ERROR' || s === 'FAILED' || s === 'UPLOAD_FAILED';
      });
    }

    if (selectedExamId !== 'all') {
      list = list.filter((j) => (j.examId || '') === selectedExamId);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((j) => {
        const title = (j.paperTitle || '').toLowerCase();
        const fileName = (j.fileName || '').toLowerCase();
        const examName = (j.examTitle || j.examShortName || j.examId || '').toLowerCase();
        const year = (j.paperYear ? String(j.paperYear) : '');
        return title.includes(q) || fileName.includes(q) || examName.includes(q) || year.includes(q);
      });
    }

    return list;
  }, [uploads, statusFilter, selectedExamId, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUploads.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedUploads = filteredUploads.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  if (examsLoading) return <PageSkeleton />;
  if (examsError) return <ErrorState message={examsError} />;

  return (
    <>
      <div className="section-title">
        <div>
          <p className="eyebrow">Question Repository</p>
          <h2>Question Banks</h2>
          <p>Manage your institute's custom question banks and browse Prepgen.ai managed question pools.</p>
        </div>
        <Button onClick={() => setShowUploadDialog(true)}>
          + Upload Question Paper
        </Button>
      </div>

      {/* --- Section 1: Institute Question Banks (B2B Uploads) --- */}
      <div style={{ marginBottom: 36 }}>
        {/* Metric Summary Strip */}
        <div className="metrics" style={{ marginBottom: 20 }}>
          <Card className="metric">
            <p>Uploaded Banks</p>
            <h2>{stats.total}</h2>
            <small style={{ color: 'var(--blue)' }}>{stats.published} published / live</small>
          </Card>
          <Card className="metric">
            <p>Total Questions</p>
            <h2>{stats.totalQuestions}</h2>
            <small style={{ color: 'var(--green)' }}>Extracted across all papers</small>
          </Card>
          <Card className="metric">
            <p>Needs Review</p>
            <h2 style={{ color: stats.needsReview > 0 ? 'var(--amber)' : 'inherit' }}>{stats.needsReview}</h2>
            <small style={{ color: stats.needsReview > 0 ? 'var(--amber)' : 'var(--green)' }}>
              {stats.needsReview > 0 ? 'Papers with unverified keys' : 'All keys verified'}
            </small>
          </Card>
          <Card className="metric">
            <p>Processing / Errors</p>
            <h2 style={{ color: stats.errors > 0 ? 'var(--red)' : stats.processing > 0 ? 'var(--blue)' : 'inherit' }}>
              {stats.processing + stats.errors}
            </h2>
            <small style={{ color: stats.errors > 0 ? 'var(--red)' : 'var(--muted)' }}>
              {stats.errors > 0 ? `${stats.errors} failed (retry available)` : stats.processing > 0 ? `${stats.processing} in progress` : 'All jobs finished'}
            </small>
          </Card>
        </div>

        {/* Toolbar & Filter Bar */}
        <div className="bank-toolbar">
          <div className="bank-toolbar-filters">
            <div className="tabs" style={{ margin: 0, padding: 0, border: 0 }}>
              <button
                className={statusFilter === 'all' ? 'selected' : ''}
                onClick={() => { setStatusFilter('all'); setPage(0); }}
              >
                All ({stats.total})
              </button>
              <button
                className={statusFilter === 'published' ? 'selected' : ''}
                onClick={() => { setStatusFilter('published'); setPage(0); }}
              >
                Published ({stats.published})
              </button>
              <button
                className={statusFilter === 'draft' ? 'selected' : ''}
                onClick={() => { setStatusFilter('draft'); setPage(0); }}
              >
                Drafts ({stats.drafts})
              </button>
              {stats.needsReview > 0 && (
                <button
                  className={statusFilter === 'needs_review' ? 'selected' : ''}
                  onClick={() => { setStatusFilter('needs_review'); setPage(0); }}
                >
                  ⚠️ Needs Review ({stats.needsReview})
                </button>
              )}
              {stats.processing > 0 && (
                <button
                  className={statusFilter === 'processing' ? 'selected' : ''}
                  onClick={() => { setStatusFilter('processing'); setPage(0); }}
                >
                  <Spinner /> Processing ({stats.processing})
                </button>
              )}
              {stats.errors > 0 && (
                <button
                  className={statusFilter === 'error' ? 'selected' : ''}
                  onClick={() => { setStatusFilter('error'); setPage(0); }}
                >
                  ❌ Failed ({stats.errors})
                </button>
              )}
            </div>
          </div>

          <div className="bank-toolbar-actions">
            {exams.length > 1 && (
              <select
                className="bank-exam-filter"
                value={selectedExamId}
                onChange={(e) => { setSelectedExamId(e.target.value); setPage(0); }}
                aria-label="Filter by exam"
              >
                <option value="all">All Enrolled Exams</option>
                {exams.map((exam) => (
                  <option key={exam.id || exam.examId} value={exam.examId || exam.id}>
                    {exam.shortName || exam.title}
                  </option>
                ))}
              </select>
            )}

            <div className="bank-search" style={{ minWidth: 220 }}>
              <input
                type="text"
                placeholder="Search banks, files, years…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
            </div>

            <div className="view-toggle">
              <button
                type="button"
                className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Table view"
              >
                ☰ Table
              </button>
              <button
                type="button"
                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                ⊞ Grid
              </button>
            </div>
          </div>
        </div>

        {uploadsLoading ? (
          <div className="page-loading" style={{ padding: 36 }}><Spinner /> Loading institute question banks…</div>
        ) : uploadsError ? (
          <ErrorState message={uploadsError} />
        ) : uploads.length === 0 ? (
          <Card className="pack" style={{ minHeight: 'auto', padding: 28, background: '#fafbfc' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 4px' }}>No institute question papers uploaded yet</h4>
                <p className="muted" style={{ fontSize: 13, maxWidth: 540 }}>
                  Upload previous year papers, test series, or practice sheets in PDF format. PrepGen automatically extracts questions, diagrams, and answer keys into an editable draft bank.
                </p>
              </div>
              <Button onClick={() => setShowUploadDialog(true)}>
                + Upload Questions
              </Button>
            </div>
          </Card>
        ) : filteredUploads.length === 0 ? (
          <EmptyState
            title="No matching question banks"
            action={
              <Button variant="secondary" onClick={() => { setSearch(''); setStatusFilter('all'); setSelectedExamId('all'); }}>
                Clear filters
              </Button>
            }
          >
            No question banks matched your active search or filters.
          </EmptyState>
        ) : viewMode === 'table' ? (
          /* --- Table View (Scalable for many jobs) --- */
          <Card className="table-card" style={{ marginTop: 14 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: 220 }}>Question Paper / Title</th>
                  <th>Target Exam</th>
                  <th>Status & Phase</th>
                  <th>Questions</th>
                  <th>Verification</th>
                  <th>Uploaded</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedUploads.map((job) => {
                  const jobId = job.jobId || job.id;
                  const title = job.paperTitle || job.fileName || 'Question Paper';
                  const examName = job.examTitle || job.examShortName || job.examId || 'Exam Pool';
                  const questionsCount = job.totalQuestionsWritten ?? job.totalQuestionsExtracted ?? 0;
                  const needsReviewCount = job.totalNeedsReview ?? 0;
                  const status = (job.status || '').toUpperCase();
                  const isFailed = status === 'ERROR' || status === 'FAILED' || status === 'UPLOAD_FAILED';
                  const isRetrying = retryingJobId === jobId;

                  return (
                    <tr
                      key={jobId}
                      className="clickable"
                      onClick={() => navigate(`/question-banks/uploads/${jobId}`)}
                    >
                      <td>
                        <b style={{ color: 'var(--ink)', fontSize: 13, display: 'block', marginBottom: 2 }}>
                          {title}
                        </b>
                        <small className="muted">
                          📄 {job.fileName || 'document.pdf'}
                          {job.paperYear ? ` · ${job.paperYear}` : ''}
                          {job.paperType === 'previous_year_paper' ? ' · Previous Year' : ''}
                        </small>
                      </td>
                      <td>
                        <span className="exam-pill">{examName}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {getStatusChip(job)}
                          {getReviewStatusChip(job)}
                        </div>
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{questionsCount}</span>
                        {job.pagesProcessed ? (
                          <small className="muted">from {job.pagesProcessed} pages</small>
                        ) : null}
                      </td>
                      <td>
                        {needsReviewCount > 0 ? (
                          <span className="badge-warning" style={{ fontSize: 11 }}>
                            ⚠️ {needsReviewCount} need review
                          </span>
                        ) : job.totalValidationErrors > 0 ? (
                          <span className="badge-danger" style={{ fontSize: 11 }}>
                            {job.totalValidationErrors} issues
                          </span>
                        ) : questionsCount > 0 ? (
                          <span className="badge-success" style={{ fontSize: 11 }}>
                            ✓ Verified
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <small className="muted">
                          {job.createdAt ? new Date(job.createdAt).toLocaleDateString() : '—'}
                        </small>
                      </td>
                      <td className="action-cell">
                        <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
                          {isFailed && (
                            <Button
                              variant="secondary"
                              style={{ padding: '5px 10px', fontSize: 11 }}
                              disabled={isRetrying}
                              onClick={(e) => handleRetryJob(e, jobId)}
                              title="Retry extraction pipeline"
                            >
                              {isRetrying ? <Spinner /> : 'Retry ↺'}
                            </Button>
                          )}
                          <Button
                            style={{ padding: '5px 11px', fontSize: 11 }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/question-banks/uploads/${jobId}`); }}
                          >
                            Review & Publish →
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="question-pagination" style={{ padding: '12px 16px', background: '#fafbfc', borderTop: '1px solid var(--line)', margin: 0 }}>
                <Button variant="secondary" onClick={() => setPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0}>
                  ← Prev
                </Button>
                <span>
                  Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, filteredUploads.length)} of {filteredUploads.length} banks
                </span>
                <Button variant="secondary" onClick={() => setPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage >= totalPages - 1}>
                  Next →
                </Button>
              </div>
            )}
          </Card>
        ) : (
          /* --- Grid Card View --- */
          <>
            <div className="card-grid" style={{ marginTop: 14 }}>
              {pagedUploads.map((job) => {
                const jobId = job.jobId || job.id;
                const title = job.paperTitle || job.fileName || 'Question Paper';
                const examName = job.examTitle || job.examShortName || job.examId || 'Exam Pool';
                const questionsCount = job.totalQuestionsWritten ?? job.totalQuestionsExtracted ?? 0;
                const needsReviewCount = job.totalNeedsReview ?? 0;
                const status = (job.status || '').toUpperCase();
                const isFailed = status === 'ERROR' || status === 'FAILED' || status === 'UPLOAD_FAILED';
                const isRetrying = retryingJobId === jobId;

                return (
                  <Card
                    key={jobId}
                    className="pack hoverable clickable"
                    onClick={() => navigate(`/question-banks/uploads/${jobId}`)}
                  >
                    <div className="pack-top">
                      <span className="tag">{examName}</span>
                      {getReviewStatusChip(job)}
                    </div>

                    <h2>{title}</h2>

                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', margin: '4px 0 10px' }}>
                      {getStatusChip(job)}
                      {needsReviewCount > 0 && (
                        <span className="badge-warning" style={{ fontSize: 10, padding: '3px 6px' }}>
                          ⚠️ {needsReviewCount} needs review
                        </span>
                      )}
                    </div>

                    <p className="muted" style={{ fontSize: 12 }}>
                      <b>{questionsCount}</b> questions extracted
                      {job.pagesProcessed ? ` from ${job.pagesProcessed} pages` : ''}
                      {job.createdAt ? ` · ${new Date(job.createdAt).toLocaleDateString()}` : ''}
                    </p>

                    <footer className="pack-actions">
                      {isFailed && (
                        <Button
                          variant="secondary"
                          style={{ padding: '6px 10px', fontSize: 11 }}
                          disabled={isRetrying}
                          onClick={(e) => handleRetryJob(e, jobId)}
                        >
                          {isRetrying ? <Spinner /> : 'Retry ↺'}
                        </Button>
                      )}
                      <Button onClick={(e) => { e.stopPropagation(); navigate(`/question-banks/uploads/${jobId}`); }}>
                        Review & Publish →
                      </Button>
                    </footer>
                  </Card>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="question-pagination" style={{ marginTop: 16 }}>
                <Button variant="secondary" onClick={() => setPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0}>
                  ← Prev
                </Button>
                <span>Page {currentPage + 1} / {totalPages}</span>
                <Button variant="secondary" onClick={() => setPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage >= totalPages - 1}>
                  Next →
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* --- Section 2: PrepGen Managed Question Banks --- */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Prepgen.ai Managed Question Banks</h3>
            <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>Global question pools for your enrolled exams</p>
          </div>
        </div>

        {!exams.length ? (
          <EmptyState
            title="No enrolled exams yet"
            action={<Button variant="secondary" onClick={() => navigate('/exam-library')}>Open exam library</Button>}
          >
            Add an exam from the exam library to browse its question bank here.
          </EmptyState>
        ) : (
          <div className="card-grid">
            {exams.map((exam) => (
              <Card
                key={exam.id || exam.examId}
                className="pack hoverable clickable"
                onClick={() => navigate(`/question-banks/${exam.examId || exam.id}`)}
              >
                <div className="pack-top">
                  <span className="tag">{exam.shortName || 'Exam'}</span>
                  <span className="muted">Prepgen.ai managed</span>
                </div>
                <h2>{exam.title || exam.name}</h2>
                <p>Search and inspect questions by section and topic.</p>
                <footer className="pack-actions">
                  <Button onClick={(e) => { e.stopPropagation(); navigate(`/question-banks/${exam.examId || exam.id}`); }}>
                    Browse questions
                  </Button>
                  <Button variant="secondary" onClick={(e) => { e.stopPropagation(); navigate(`/exam-library/${exam.examId || exam.id}`); }}>
                    Exam structure
                  </Button>
                </footer>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showUploadDialog && (
        <UploadBankDialog
          onClose={() => setShowUploadDialog(false)}
          onUploaded={() => {
            loadUploads();
          }}
        />
      )}
    </>
  );
}
